import { getProductsSearch } from "@/actions";
import { useSetQuery } from "@/hooks";
import { ProductsSearchState, SearchParams } from "@/types";
import { useActionState, useCallback, useEffect, useRef, useState } from "react";

export const useProductsSearch = (params: SearchParams) => {
  const initialState: ProductsSearchState = { params, query: "", products: [] };
  const [state, action, isPending] = useActionState(getProductsSearch, initialState);
  const { setQuery, isPending: isQueryPending } = useSetQuery();
  const [value, setValue] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const q = value.trim();
  const isQueryReady = q.length >= 3;
  const isFresh = state.query === q;
  const hasProducts = isFresh && state.products.length > 0;
  const showNotFound = isQueryReady && isFresh && !isPending && state.products.length === 0;
  const open = isQueryReady && (isPending || hasProducts || showNotFound);

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (!isQueryReady) return;
    timer.current = window.setTimeout(() => {
      formRef.current?.requestSubmit();
    }, 300);
    return () => window.clearTimeout(timer.current);
  }, [isQueryReady, q]);

  const handleProductClick = useCallback(
    (productId: string) => {
      setValue("");
      setQuery({ product_id: productId }, { reset: true });
    },
    [setQuery],
  );

  return {
    open,
    state,
    action,
    isPending,
    isQueryPending,
    formRef,
    handleProductClick,
    value,
    setValue,
    hasProducts,
    showNotFound,
  };
};
